import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ProgressRing } from "../components/ProgressRing";
import { StatusBadge } from "../components/StatusBadge";
import { formatDate } from "../lib/format";
import {
  calculatePhaseProgress,
  calculateStatusCounts,
  calculateWeightedProgress,
} from "../lib/progress";
import { statusDefinitions } from "../lib/status";
import type { Milestone, Project, ProjectSnapshot, WorkItem, WorkStatus } from "../types/domain";

interface OverviewViewProps {
  project: Project;
  items: WorkItem[];
  milestones: Milestone[];
  snapshots: ProjectSnapshot[];
}

export function OverviewView({ project, items, milestones, snapshots }: OverviewViewProps) {
  const progress = calculateWeightedProgress(items);
  const phaseProgress = calculatePhaseProgress(items);
  const counts = calculateStatusCounts(items);
  const phases = items.filter((item) => item.itemType === "phase" && item.parentId === null);
  const focusItems = items.filter((item) => item.isCurrentFocus);
  const statusChart = Object.entries(counts).map(([status, count]) => ({
    status: statusDefinitions[status as WorkStatus].shortLabel,
    count,
  }));
  const snapshotChart = snapshots.map((snapshot) => ({
    date: formatDate(snapshot.capturedAt, { month: "short", day: "numeric" }),
    progress: snapshot.progressPercent,
    label: typeof snapshot.metadata.label === "string" ? snapshot.metadata.label : "Snapshot",
  }));

  return (
    <div className="view-stack">
      <section className="overview-grid">
        <article className="panel progress-panel">
          <div>
            <span className="eyebrow">Progreso ponderado · modelo v1</span>
            <h2>{project.name}</h2>
            <p>{project.vision}</p>
          </div>
          <ProgressRing value={progress.percent} label="roadmap conocido" />
          <footer>
            <span>{progress.consideredItems} elementos hoja</span>
            <span>
              {progress.weightedCompleted.toFixed(1)} / {progress.weightedTotal.toFixed(1)} puntos
            </span>
          </footer>
        </article>

        <article className="panel focus-panel">
          <span className="eyebrow">Foco actual</span>
          {focusItems.length === 0 ? (
            <p className="muted">No hay un bloque marcado como foco.</p>
          ) : (
            focusItems.map((item) => (
              <div className="focus-card" key={item.id}>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
                <StatusBadge status={item.status} />
              </div>
            ))
          )}
          <div className="boundary-callout">
            <strong>Frontera</strong>
            <p>
              El dashboard gestiona estado y enlaces. No toca evidencia de replay, datasets ni
              trading_enabled.
            </p>
          </div>
        </article>
      </section>

      <section className="metrics-grid">
        <article className="panel chart-panel">
          <header>
            <div>
              <span className="eyebrow">Evolución</span>
              <h3>Snapshots de progreso</h3>
            </div>
          </header>
          <div className="chart-frame">
            {snapshotChart.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={snapshotChart}
                  margin={{ top: 12, right: 14, bottom: 0, left: -20 }}
                >
                  <CartesianGrid stroke="rgba(114, 167, 184, .12)" vertical={false} />
                  <XAxis dataKey="date" stroke="#7c9baa" tickLine={false} axisLine={false} />
                  <YAxis domain={[0, 100]} stroke="#7c9baa" tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{
                      background: "#0d2633",
                      border: "1px solid #204555",
                      borderRadius: 12,
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="progress"
                    stroke={project.accent}
                    strokeWidth={3}
                    dot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-chart">Todavía no existen snapshots.</div>
            )}
          </div>
        </article>

        <article className="panel chart-panel">
          <header>
            <div>
              <span className="eyebrow">Distribución</span>
              <h3>Estados del roadmap</h3>
            </div>
          </header>
          <div className="chart-frame">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusChart} margin={{ top: 12, right: 8, bottom: 40, left: -20 }}>
                <CartesianGrid stroke="rgba(114, 167, 184, .12)" vertical={false} />
                <XAxis
                  dataKey="status"
                  angle={-25}
                  textAnchor="end"
                  interval={0}
                  height={60}
                  stroke="#7c9baa"
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis allowDecimals={false} stroke="#7c9baa" tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{
                    background: "#0d2633",
                    border: "1px solid #204555",
                    borderRadius: 12,
                  }}
                />
                <Bar dataKey="count" fill={project.accent} radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </article>
      </section>

      <section className="panel">
        <header className="panel__header">
          <div>
            <span className="eyebrow">Fases</span>
            <h3>Progreso por fase</h3>
          </div>
        </header>
        <div className="phase-list">
          {phases.map((phase) => {
            const value = phaseProgress[phase.id] ?? 0;
            return (
              <div className="phase-row" key={phase.id}>
                <div>
                  <strong>{phase.title}</strong>
                  <StatusBadge status={phase.status} compact />
                </div>
                <div className="progress-bar">
                  <span style={{ width: `${value}%`, background: project.accent }} />
                </div>
                <strong>{value.toFixed(1)}%</strong>
              </div>
            );
          })}
        </div>
      </section>

      <section className="panel">
        <header className="panel__header">
          <div>
            <span className="eyebrow">Puertas visibles</span>
            <h3>Hitos</h3>
          </div>
        </header>
        <div className="milestone-grid">
          {milestones.map((milestone) => (
            <article className="milestone-card" key={milestone.id}>
              <div>
                <StatusBadge status={milestone.status} compact />
                <span className="milestone-card__date">
                  {formatDate(milestone.completedAt ?? milestone.targetDate)}
                </span>
              </div>
              <h4>{milestone.title}</h4>
              <p>{milestone.description}</p>
            </article>
          ))}
        </div>
      </section>

      <p className="roadmap-disclaimer">{project.roadmapDisclaimer}</p>
    </div>
  );
}
